import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { styleMap } from 'lit/directives/style-map.js'

@customElement('schmancy-progress')
export default class SchmancyProgress extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}

			/* The indeterminate sweep is a pill whose left end travels -30% → 40% → 100%
			   of the track while its length breathes 20 → 35 → 30 → 25 → 20%. It never
			   ends, so it animates transform and opacity alone: no frame restyles, lays
			   out or paints. Each quantity keeps its own keyframe offsets, because the
			   easing applies per segment between the offsets that name a property.

			   travel — the whole sweep, carried by its left end (and the fade). */
			@keyframes indeterminate-travel {
				0% {
					transform: translateX(-30%);
					opacity: 0.6;
				}
				25% {
					opacity: 1;
				}
				50% {
					transform: translateX(40%);
				}
				75% {
					opacity: 0.9;
				}
				100% {
					transform: translateX(100%);
					opacity: 0.6;
				}
			}

			/* reach — the right end's distance from the left end: the sweep's length. */
			@keyframes indeterminate-reach {
				0% {
					transform: translateX(20%);
				}
				25% {
					transform: translateX(35%);
				}
				50% {
					transform: translateX(30%);
				}
				75% {
					transform: translateX(25%);
				}
				100% {
					transform: translateX(20%);
				}
			}

			/* half — the straight run between the two round ends is two overlapping
			   halves, one hung off each end, each half the sweep's length. A single
			   scaled run would squash the ends' radius into a square corner. */
			@keyframes indeterminate-half {
				0% {
					transform: scaleX(0.1);
				}
				25% {
					transform: scaleX(0.175);
				}
				50% {
					transform: scaleX(0.15);
				}
				75% {
					transform: scaleX(0.125);
				}
				100% {
					transform: scaleX(0.1);
				}
			}
		`,
	]

	@property({ type: Number, reflect: true })
	value = 0

	@property({ type: Number, reflect: true })
	max = 100

	@property({ type: Boolean, reflect: true })
	indeterminate = false

	// M3 aligned track heights: xs=1px, sm=2px, md=4px, lg=8px
	@property({ type: String, reflect: true })
	size: 'xs' | 'sm' | 'md' | 'lg' = 'md'

	@property({ type: String, reflect: true })
	color: 'primary' | 'secondary' | 'tertiary' | 'error' | 'success' = 'primary'

	@property({ type: Boolean, reflect: true })
	glass = false

	private get percentage(): number {
		// A NaN width is an invalid declaration the browser drops, leaving the
		// fill at its auto width — a FULL bar. An empty whole and an
		// unparseable value both read 0% rather than 100%.
		if (!(this.max > 0) || !Number.isFinite(this.value)) return 0
		return Math.min(100, Math.max(0, (this.value / this.max) * 100))
	}

	protected render() {
		const containerClasses = {
			'w-full': true,
			relative: true,
			'overflow-hidden': true,
			'rounded-full': true,
			'h-px': this.size === 'xs',
			'h-0.5': this.size === 'sm',
			'h-1': this.size === 'md',
			'h-2': this.size === 'lg',
			'backdrop-blur-[var(--schmancy-sys-glass-blur)]': this.glass,
			'backdrop-saturate-[var(--schmancy-sys-glass-saturate)]': this.glass,
			'bg-surface-container/20': this.glass && !this.indeterminate,
			'bg-surface-container': !this.glass,
			'shadow-[inset_0_1px_2px_0_rgba(0,0,0,0.1)]': this.glass,
			border: this.glass,
			'border-outline/20': this.glass,
		}

		const ink = {
			'bg-primary-default': this.color === 'primary',
			'bg-secondary-default': this.color === 'secondary',
			'bg-tertiary-default': this.color === 'tertiary',
			'bg-error-default': this.color === 'error',
			'bg-success-default': this.color === 'success',
		}

		if (this.indeterminate) {
			// The pieces overlap, so each is opaque and every translucency is applied
			// once, to the assembled pill. Glass was ink at 70% under a surface-on
			// sheen fading 20% → 0 top to bottom: the same composite is the pill masked
			// 76% → 70% (0.7 + 0.3 × sheen) over opaque pieces whose sheen is 26.3% → 0
			// (sheen ÷ that mask). Each round end is the bar's height wide, but never
			// more than 9% of the track: the shortest sweep is 20%, so the two halves
			// hung off the ends always meet inside the pill, never past an end's curve.
			// The straight runs overhang the track a pixel top and
			// bottom: a composited layer is not snapped to device pixels as painted
			// layout is, and the track's clip squares the overhang back to its edge.
			const piece = { ...ink, 'bg-linear-to-b from-surface-on/[26.3%] to-transparent': this.glass }
			return html`
				<div class=${this.classMap(containerClasses)}>
					<div
						class="absolute inset-0 [container-type:size] animate-[indeterminate-travel_1.8s_cubic-bezier(0.34,1.2,0.64,1)_infinite]"
						role="progressbar"
						aria-valuenow="${Number.isFinite(this.value) ? this.value : 0}"
						aria-valuemin="0"
						aria-valuemax="${this.max}"
						aria-label="${ifDefined(this.ariaLabel || undefined)}"
					>
						<div
							class=${this.classMap({
								'absolute inset-0': true,
								'[mask-image:linear-gradient(rgb(0_0_0/0.76),rgb(0_0_0/0.7))]': this.glass,
							})}
						>
							<div
								class=${this.classMap({
									'absolute inset-y-0 left-0 w-[min(100cqh,9cqw)] rounded-full': true,
									...piece,
								})}
							></div>
							<div
								class=${this.classMap({
									'absolute -inset-y-px left-[min(50cqh,4.5cqw)] w-full origin-left animate-[indeterminate-half_1.8s_cubic-bezier(0.34,1.2,0.64,1)_infinite]': true,
									...piece,
								})}
							></div>
							<div class="absolute inset-0 animate-[indeterminate-reach_1.8s_cubic-bezier(0.34,1.2,0.64,1)_infinite]">
								<div
									class=${this.classMap({
										'absolute inset-y-0 right-full w-[min(100cqh,9cqw)] rounded-full': true,
										...piece,
									})}
								></div>
								<div
									class=${this.classMap({
										'absolute -inset-y-px right-[calc(100%+min(50cqh,4.5cqw))] w-full origin-right animate-[indeterminate-half_1.8s_cubic-bezier(0.34,1.2,0.64,1)_infinite]': true,
										...piece,
									})}
								></div>
							</div>
						</div>
					</div>
				</div>
			`
		}

		const barClasses = {
			'h-full': true,
			'rounded-full': true,
			'transition-all': true,
			'duration-300': true,
			'ease-in-out': true,
			relative: true,
			...(this.glass ? {} : ink),
		}

		const glassBarClasses = {
			'backdrop-blur-sm': this.glass,
			'shadow-[0_0_20px_rgba(0,0,0,0.1)]': this.glass,
			'bg-primary-default/70': this.glass && this.color === 'primary',
			'bg-secondary-default/70': this.glass && this.color === 'secondary',
			'bg-tertiary-default/70': this.glass && this.color === 'tertiary',
			'bg-error-default/70': this.glass && this.color === 'error',
			'bg-success-default/70': this.glass && this.color === 'success',
		}

		return html`
			<div class=${this.classMap(containerClasses)}>
				<div
					class=${this.classMap({ ...barClasses, ...glassBarClasses })}
					style=${styleMap({ width: `${this.percentage}%` })}
					role="progressbar"
					aria-valuenow="${Number.isFinite(this.value) ? this.value : 0}"
					aria-valuemin="0"
					aria-valuemax="${this.max}"
					aria-label="${ifDefined(this.ariaLabel || undefined)}"
				>
					${this.glass
						? html` <div class="absolute inset-0 bg-linear-to-b from-surface-on/20 to-transparent rounded-full"></div> `
						: ''}
				</div>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-progress': SchmancyProgress
	}
}
